import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { api, clearToken, getToken } from "@/api";
import { toast } from "@/hooks/use-toast";

// ------------------ AUTH CONTEXT ------------------
const AuthContext = React.createContext(null);

export function AuthProvider({ children }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const token = getToken();
    if (!token) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setMe(res.data);
    } catch (e) {
      clearToken();
      setMe(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    clearToken();
    setMe(null);
    toast({ title: "Déconnecté" });
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ me, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("AuthContext missing");
  return ctx;
}

export function ProtectedRoute({ children, role }) {
  const { me, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        data-testid="app-loading"
        className="min-h-screen bg-[#060A14] text-slate-50"
      >
        <div className="mx-auto max-w-6xl px-6 py-10">
          <Card
            data-testid="loading-card"
            className="border-white/10 bg-white/[0.08]"
          >
            <CardHeader>
              <CardTitle data-testid="loading-title" className="text-slate-100">
                Chargement…
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  if (!me) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Gate obligatoire : première connexion → doit changer son MDP avant toute route protégée
  if (me.must_change_password) {
    return <Navigate to="/first-password" replace />;
  }

  if (role && me.role !== role) {
    return <Navigate to={me.role === "admin" ? "/admin" : "/owner"} replace />;
  }

  return children;
}
